import React, { Fragment, type FunctionComponent } from 'react'
import { Text, colors, spacing } from '@retailer-platform/shared-components'
import { MerchandisingI18nFormFieldContainer } from 'domains/merchandising/components/i18n-form-field-container/MerchandisingI18nFormFieldContainer'
import { MerchandisingFormField } from 'domains/merchandising/components/form-field/MerchandisingFormField'
import { MerchandisingFormRow } from 'domains/merchandising/components/form-row/MerchandisingFormRow'
import { MerchandisingFormInput } from 'domains/merchandising/components/form-input/MerchandisingFormInput'
import { MerchandisingFormSelect } from 'domains/merchandising/components/form-input/MerchandisingFormSelect'
import { MerchandisingFormTextArea } from 'domains/merchandising/components/form-input/MerchandisingFormTextArea'
import { MerchandisingFormColorInput } from 'domains/merchandising/components/form-input/MerchandisingFormColorInput'
import { ctaColorOptions } from 'domains/merchandising/constants/heroBanner.constants'
import { type ControllerProps } from 'utils/react-hook-form/reactHookForm.types'
import { useSelectOptionsDashMessage } from 'domains/merchandising/utils/intl.hooks'
import { useDashMessage } from 'intl/intl.hooks'

interface Props extends Omit<ControllerProps, 'name'> {}

const footnoteWrapper = {
  margin: `-${spacing.X12} 0 ${spacing.X16} 0`,
}

const footnoteStyles = {
  color: colors.GRAYSCALE.X70,
  margin: 0,
}

export const HeroBannerFormContentText: FunctionComponent<Props> = ({ form }) => {
  const { register, getValues } = form
  const selectOptions = useSelectOptionsDashMessage(ctaColorOptions)
  const footnote = useDashMessage('merchandising.hero-banners.section.content.description.footnote')

  return (
    <Fragment>
      <MerchandisingFormRow>
        <MerchandisingFormField>
          <MerchandisingI18nFormFieldContainer
            form={form}
            labelPrefixed
            field={locale => (
              <MerchandisingFormInput
                identifier={`headingText.text.${locale}`}
                defaultValue={getValues(`headingText.text.${locale}`)}
                ref={register}
                labelId="merchandising.hero-banners.section.content.heading.label"
                maxLength={60}
                showLength
              />
            )}
          />
        </MerchandisingFormField>

        <MerchandisingFormField>
          <MerchandisingI18nFormFieldContainer
            form={form}
            field={locale => (
              <MerchandisingFormColorInput
                identifier={`headingText.color.${locale}`}
                defaultValue={getValues(`headingText.color.${locale}`)}
                ref={register}
                labelId="merchandising.hero-banners.section.content.heading.color.label"
              />
            )}
          />
        </MerchandisingFormField>
      </MerchandisingFormRow>

      <MerchandisingFormRow>
        <MerchandisingFormField>
          <MerchandisingI18nFormFieldContainer
            form={form}
            labelPrefixed
            field={locale => (
              <MerchandisingFormInput
                identifier={`subheadingText.text.${locale}`}
                defaultValue={getValues(`subheadingText.text.${locale}`)}
                ref={register}
                labelId="merchandising.hero-banners.section.content.subheading.label"
                maxLength={24}
                showLength
              />
            )}
          />
        </MerchandisingFormField>

        <MerchandisingFormField>
          <MerchandisingI18nFormFieldContainer
            form={form}
            field={locale => (
              <MerchandisingFormColorInput
                identifier={`subheadingText.color.${locale}`}
                defaultValue={getValues(`subheadingText.color.${locale}`)}
                ref={register}
                labelId="merchandising.hero-banners.section.content.subheading.color.label"
              />
            )}
          />
        </MerchandisingFormField>
      </MerchandisingFormRow>

      <MerchandisingFormRow>
        <MerchandisingFormField>
          <MerchandisingI18nFormFieldContainer
            form={form}
            labelPrefixed
            field={locale => (
              <MerchandisingFormTextArea
                identifier={`descriptionText.text.${locale}`}
                defaultValue={getValues(`descriptionText.text.${locale}`)}
                ref={register}
                labelId="merchandising.hero-banners.section.content.description.label"
                maxLength={90}
                showLength
              />
            )}
          />
        </MerchandisingFormField>

        <MerchandisingFormField>
          <MerchandisingI18nFormFieldContainer
            form={form}
            field={locale => (
              <MerchandisingFormColorInput
                identifier={`descriptionText.color.${locale}`}
                defaultValue={getValues(`descriptionText.color.${locale}`)}
                ref={register}
                labelId="merchandising.hero-banners.section.content.description.color.label"
              />
            )}
          />
        </MerchandisingFormField>
      </MerchandisingFormRow>
      <div css={footnoteWrapper}>
        <Text css={footnoteStyles}>{footnote}</Text>
      </div>

      <MerchandisingFormRow>
        <MerchandisingFormField>
          <MerchandisingI18nFormFieldContainer
            form={form}
            labelPrefixed
            field={locale => (
              <MerchandisingFormInput
                identifier={`button.label.${locale}`}
                defaultValue={getValues(`button.label.${locale}`)}
                ref={register}
                labelId="merchandising.hero-banners.section.content.cta.label"
                maxLength={20}
                showLength
              />
            )}
          />
        </MerchandisingFormField>

        <MerchandisingFormField>
          <MerchandisingI18nFormFieldContainer
            form={form}
            field={locale => (
              <MerchandisingFormSelect
                isDisabled
                form={form}
                identifier={`button.styleVariant.${locale}`}
                defaultValue={getValues(`button.styleVariant.${locale}`)}
                options={selectOptions}
                labelId={'merchandising.hero-banners.section.content.cta.color.label'}
              />
            )}
          />
        </MerchandisingFormField>
      </MerchandisingFormRow>

      <MerchandisingFormRow>
        <MerchandisingFormField>
          <MerchandisingI18nFormFieldContainer
            form={form}
            field={locale => (
              <MerchandisingFormColorInput
                identifier={`backgroundColor.${locale}`}
                defaultValue={getValues(`backgroundColor.${locale}`)}
                ref={register}
                labelId="merchandising.hero-banners.section.content.background.color.label"
              />
            )}
          />
        </MerchandisingFormField>
      </MerchandisingFormRow>
    </Fragment>
  )
}
